import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Label,
  Input,
  MultiStepTracker,
  Step,
  ButtonPrimary,
  PageLevelMessage,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
} from '@americanexpress/dls-react';
import * as regexPatterns from '../../validators/regexPatterns';
import SupplierContactInfo from './SupplierContactInfo';
import SupplierOtherInfo from './SupplierOtherInfo';
import SupllierBankingInfo from './SupplierBankingInfo';
import AmexContactInfo from './AmexContactInfo';
import ReviewPage from './ReviewPage';
import SuccessPage from './SuccessPage';
import * as staticDataService from '../../services/staticDataService';
import * as profileDataService from '../../services/profileDataService';
import {
  validateInputs,
  validateAmexContactEmailId,
} from '../../validators/vendorIntakeFormValidator';
import '../../App.css';
import HelpDialogue from '../HelpDialogue/HelpDialogue';
import { nextOrSubmit, submitOrButton } from '../../helper/functions';
import * as formDataMapper from '../../helper/mapper';
import useDocumentTitle from '../../helper/documentTitle';
import Loading from '../Util/Loading';
import * as stringConstants from '../../constants/stringConstants';
import * as reqHeaderConstants from '../../constants/reqHeaderConstants';
import ErrorBoundary from '../Util/ErrorBoundary';
import InvalidSupplier from './InvalidSupplier';

const PageDisplay = (
  page,
  setPage,
  isNextClicked,
  setIsEditClicked,
  formData,
  setFormData,
  formErrors,
  setFormErrors,
  files,
  setFiles,
  alreadyAttachedFiles,
  setAlreadyAttachedFiles,
  attachmentPlaceholder,
  staticData,
  uploadedDocs,
  setUploadedDocs,
  deletedDocGuids,
  setDeletedDocGuids,
  isAcknowledged,
  setIsAcknowledged
) => {
  if (page === -2) {
    return <InvalidSupplier />;
  }

  if (page === -1) {
    return <ErrorBoundary />;
  }
  if (page === 1) {
    return (
      <SupplierContactInfo
        page={page}
        isNextClicked={isNextClicked}
        formData={formData}
        setFormData={setFormData}
        formErrors={formErrors}
        setFormErrors={setFormErrors}
        staticData={staticData}
        isAcknowledged={isAcknowledged}
        setIsAcknowledged={setIsAcknowledged}
      />
    );
  }
  if (page === 2) {
    return (
      <SupplierOtherInfo
        page={page}
        isNextClicked={isNextClicked}
        formData={formData}
        setFormData={setFormData}
        formErrors={formErrors}
        setFormErrors={setFormErrors}
        files={files}
        setFiles={setFiles}
        attachmentPlaceholder={attachmentPlaceholder}
        staticData={staticData}
      />
    );
  }
  if (page === 3) {
    return (
      <SupllierBankingInfo
        page={page}
        isNextClicked={isNextClicked}
        formData={formData}
        setFormData={setFormData}
        formErrors={formErrors}
        setFormErrors={setFormErrors}
        files={files}
        setFiles={setFiles}
        alreadyAttachedFiles={alreadyAttachedFiles}
        setAlreadyAttachedFiles={setAlreadyAttachedFiles}
        staticData={staticData}
        uploadedDocs={uploadedDocs}
        setUploadedDocs={setUploadedDocs}
        deletedDocGuids={deletedDocGuids}
        setDeletedDocGuids={setDeletedDocGuids}
      />
    );
  }
  if (page === 4) {
    return (
      <AmexContactInfo
        page={page}
        isNextClicked={isNextClicked}
        formData={formData}
        setFormData={setFormData}
        formErrors={formErrors}
        setFormErrors={setFormErrors}
      />
    );
  }
  if (page === 5) {
    return (
      <ReviewPage
        page={page}
        setIsEditClicked={setIsEditClicked}
        setPage={setPage}
        formData={formData}
        setFormData={setFormData}
        files={files}
        uploadedDocs={uploadedDocs}
        alreadyAttachedFiles={alreadyAttachedFiles}
      />
    );
  }

  return <SuccessPage />;
};

// TODO: Fix ES Lint errors.
const VendorIntakeForm = () => {
  useDocumentTitle('Supplier Registration');
  const [page, setPage] = useState(0);
  const [formErrors, setFormErrors] = useState({});
  const [files, setFiles] = useState([]);
  const [alreadyAttachedFiles, setAlreadyAttachedFiles] = useState([]);
  const [showDraftSuccess, setShowDraftSuccess] = useState(false);
  const [isNextClicked, setIsNextClicked] = useState(false);
  const [isEditClicked, setIsEditClicked] = useState(false);
  const [isInitialDataLoaded, setIsInitialDataLoaded] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();
  const [showDrafterEmailModal, setShowDrafterEmailModal] = useState(false);
  const [drafterEmailValidator, setDrafterEmailValidator] = useState('');
  const [uploadedDocs, setUploadedDocs] = useState([]);
  const id = searchParams.get('id');
  const [showValidationsModal, setShowValidationsModal] = useState(false);
  const [supplierId, setSupplierId] = useState(
    id === null ? '' : encodeURIComponent(id)
  );
  const [showSubmissionLoadingModal, setShowSubmissionLoadingModal] =
    useState(false);
  const [supplier, setSupplier] = useState([]);
  const [alertNotification, setAlertNotification] = useState(<div></div>);
  const [showRequesterComments, setShowRequesterComments] = useState(false);
  const [staticData, setStaticData] = useState({
    regions: [],
    regionCountries: [],
    countries: [],
    currencies: [],
    paymentMethods: [],
    paymentTerms: [],
    accountTypes: [],
  });
  const [attachmentPlaceholder, setAttachmentPlaceholder] = useState(
    'Attach your documents by clicking Browse'
  );
  const [formData, setFormData] = useState({
    region: { RegionName: '', RegionCode: '' },
    billingCountry: { CountryName: '', CountryCode: '' },
    billingCurrency: { CurrencyName: '', 'Currency Code': '' },
    otherCountry: '',
    supplierRegisteredName: '',
    supplierRegisteredLocalName: '',
    supplierRegisteredAddress_Line1: '',
    supplierRegisteredAddress_Line2: '',
    supplierRegisteredLocalAddress_Line1: '',
    supplierRegisteredLocalAddress_Line2: '',
    city: '',
    state: '',
    postalCode: '',
    country: { CountryName: '', CountryCode: '' },
    otherCountryName: '',
    payeeName: '',
    payeeLocalName: '',
    payeeAddress_Line1: '',
    payeeAddress_Line2: '',
    payeeLocalAddress_Line1: '',
    payeeLocalAddress_Line2: '',
    payeeCity: '',
    payeeState: '',
    payeePostalCode: '',
    payeeCountry: { CountryName: '', CountryCode: '' },
    otherPayeeCountryName: '',
    isKeyFinancialDecisionMaker: 'Yes',
    supplierContactPerson: '',
    supplierEmailAddress: '',
    supplierPhoneNumber: '',
    supplierFaxNumber: '',
    drafterEmailId: '',
    isMsme: '',
    panNo: '',
    serviceTaxNo: '',
    abn: '',
    registeredState: '',
    registeredStateNo: '',
    uen: '',
    gstNo: '',
    sirenNumber: '',
    siretNumber: '',
    businessRegCertNo: '',
    fedTax: '',
    tin: '',
    isAuditFirm: '',
    paymentMethod: '',
    remittanceEmailAddress: '',
    paymentTerms: '',
    serviceEstablishment: '',
    nif: '',
    bankName: '',
    bic: '',
    branchName: '',
    branchCode: '',
    accountNumber: '',
    bankCode: '',
    accountType: '',
    aba: '',
    intermediateBankName: '',
    intermediateBankBic: '',
    intermediateBankBranchName: '',
    intermediateBankBranchCode: '',
    intermediateBankAccountNumber: '',
    intermediateABA: '',
    japanBankName: '',
    japanBranchName: '',
    bankGiroNumber: '',
    amexContactName: '',
    amexContactEmail: '',
    amexContactNumber: '',
    amexContactBusinessUnit: '',
    otherPaymentMethod: '',
  });
  const [deletedDocGuids, setDeletedDocGuids] = useState([]);
  const [isAcknowledged, setIsAcknowledged] = useState(false);

  useEffect(() => {
    let pageToBeSet = 1;
    (async () => {
      let [regions, countries, currencies, lookupData, supplier] =
        await getInitialData();
      if (!!supplierId) {
        if (
          supplier.status !== stringConstants.RETURN_TO_SUPPLIER &&
          supplier.status !== stringConstants.DRAFTED
        ) {
          pageToBeSet = -2;
        }
        setSupplier(supplier);
        try {
          setUploadedDocs(JSON.parse(supplier.docs));
        } catch {
          setUploadedDocs([]);
        }

        if (
          supplier.status === stringConstants.RETURN_TO_SUPPLIER ||
          (supplier.status === stringConstants.DRAFTED &&
            !!supplier.approverComment)
        ) {
          setAlertNotification(
            <PageLevelMessage
              className="margin-2-r margin-2-l"
              type="warning"
              dismissible={false}
              filled={true}
            >
              Your Amex contact has returned the request to you. Click{' '}
              <a onClick={onShowRequesterComments}>here</a> to view the required
              changes.
            </PageLevelMessage>
          );
        }
        let supplierFormData = formDataMapper.mapDraftData(supplier);
        setFormData(supplierFormData);
      }
      setStaticData({
        ...staticData,
        regions,
        countries,
        currencies,
        paymentMethods: lookupData?.PAYMENT_METHOD?.sort(),
        paymentTerms: lookupData?.PAYMENT_TERM,
        accountTypes: lookupData?.ACCOUNT_TYPE,
      });
      setIsInitialDataLoaded(true);
      setPage(pageToBeSet);
    })();
  }, []);

  const validationsModal = (
    <Modal onClose={() => setShowValidationsModal(false)}>
      <ModalHeader id="modal-header">
        <h2 className="fluid heading-4"></h2>
      </ModalHeader>
      <ModalBody>
        <div className="pad-3">
          <h2 className="heading-5 margin-3-b pad-3-t">
            {'Please Complete All Required Fields'}
          </h2>
          <p>{'You need to fill in all the required fields.'}</p>
        </div>
      </ModalBody>
      <ModalFooter>
        <ButtonPrimary onClick={() => setShowValidationsModal(false)}>
          Continue
        </ButtonPrimary>
      </ModalFooter>
    </Modal>
  );

  useEffect(() => {
    if (!formData.region.RegionCode || !isInitialDataLoaded) {
      return;
    }

    if (!supplierId) {
      setFormData({
        ...formData,
        billingCountry: { CountryName: '', CountryCode: '' },
      });
    }

    // set countries drop down as empty until new countries are populated
    setStaticData({
      ...staticData,
      regionCountries: [],
    });

    staticDataService
      .getCountries(formData.region?.RegionCode ?? '', true)
      .then((response) => {
        setStaticData({
          ...staticData,
          regionCountries: response.data,
        });
      })
      .catch((err) => err);
  }, [formData.region.RegionName, isInitialDataLoaded]);

  const getInitialData = async () => {
    let regionsPromise = staticDataService
      .getRegions()
      .then((response) => response.data)
      .catch((err) => handleError());

    let countriesPromise = staticDataService
      .getCountries('', true)
      .then((response) => response.data)
      .catch((err) => handleError());

    let currenciesPromise = staticDataService
      .getCurrencies()
      .then((response) => response.data)
      .catch((err) => handleError());

    let lookupDataPromise = staticDataService
      .getLookupData('ACCOUNT_TYPE,PAYMENT_TERM,PAYMENT_METHOD')
      .then((response) => response.data)
      .catch((err) => handleError());

    let supplierPromise;
    if (supplierId !== undefined) {
      supplierPromise = profileDataService
        .getDraftSupplier(supplierId)
        .then((res) => res.data)
        .catch((err) => handleError());
    }

    return await Promise.all([
      regionsPromise,
      countriesPromise,
      currenciesPromise,
      lookupDataPromise,
      supplierPromise,
    ]).catch((err) => setPage(1));
  };

  const handleError = () => {
    setPage(-1);
  };
  const handleSubmit = (event) => {
    event.preventDefault();
  };

  const onBackClick = () => {
    setPage((currPage) => currPage - 1);
    window.scrollTo(0, 0);
  };

  const onNextClick = async () => {
    let pageToBeSet = page + 1;
    setIsNextClicked((isNextClicked) => true);
    if (isEditClicked === true) {
      pageToBeSet = 5;
    }

    if (page === 5) {
      if (
        validateInputs(2, setFormErrors, formData, true) &&
        validateInputs(3, setFormErrors, formData, true)
      ) {
        submitVendor();
      } else {
        setShowValidationsModal(true);
      }
    }

    if (validateInputs(page, setFormErrors, formData, true)) {
      if (page === 4) {
        window.scrollTo(0, 0);
        setPage(0);
        const isValidAmexEmail = await validateAmexContactEmailId(
          formData.amexContactEmail,
          setFormErrors
        ).then((res) => res);
        if (isValidAmexEmail == false) {
          setPage(page);
          return;
        }
      }
      setPage(pageToBeSet);
      setIsNextClicked((isNextClicked) => false);
    }
    window.scrollTo(0, 0);
  };

  const submitVendor = async (isDraft = 'N') => {
    setShowSubmissionLoadingModal(true);
    let vendorFormData = formDataMapper.mapVendorFormData(formData, isDraft);
    if (
      isDraft === 'Y' ||
      supplier.status === stringConstants.RETURN_TO_SUPPLIER ||
      !!supplierId
    ) {
      vendorFormData['encodedSupplierId'] = supplierId;
    }
    let formBody = new FormData();
    for (let i = 0; i < files?.files?.length; i++) {
      formBody.append(`file ${i}`, files?.files[i]);
    }

    formBody.append('vendorFormData', JSON.stringify(vendorFormData));
    formBody.append('docs', JSON.stringify(deletedDocGuids));
    let supplierData = await profileDataService
      .createSupplier(formBody, reqHeaderConstants.postFormDataHeaders)
      .then((res) => {
        if (isDraft !== 'Y') {
          setPage(6);
          setAlertNotification(<div></div>);
        } else {
          setAlreadyAttachedFiles({
            ...alreadyAttachedFiles,
            files:
              [
                ...(alreadyAttachedFiles?.files ?? []),
                ...(files?.files ?? []),
              ] ?? [],
          });
          setFiles([]);
          setSupplierId(encodeURIComponent(res.data?.encodedSupplierId) ?? '');
          setShowDraftSuccess(true);
          window.scrollTo(0, 0);
        }

        return res.data;
      })
      .catch((err) => setPage(-1))
      .finally((res) => setShowSubmissionLoadingModal(false));
  };

  const onSaveAsDraftClick = () => {
    if (
      !(
        formData.drafterEmailId.trim() &&
        regexPatterns.emailRegexPattern.test(formData.drafterEmailId)
      )
    ) {
      setDrafterEmailValidator('Please enter a valid email address');
      return;
    }
    toggleDrafterEmailModal();
    return submitVendor('Y');
  };

  const onShowRequesterComments = () => {
    toggleModal();
  };

  const toggleDrafterEmailModal = () => {
    setDrafterEmailValidator('');
    setShowDrafterEmailModal(!showDrafterEmailModal);
  };

  const toggleModal = () => {
    setShowRequesterComments(!showRequesterComments);
  };

  const drafterEmailModal = (
    <Modal onClose={toggleDrafterEmailModal}>
      <ModalHeader id="modal-header">
        <h2 className="fluid heading-4"></h2>
      </ModalHeader>
      <ModalBody>
        <div className="pad-3">
          <Label className="heading-5 pad-3-t">
            <b>
              The form is being saved as draft to resume later. Enter the email
              address of the request initiator to receive the link to resume the
              form filling.
            </b>
          </Label>
          <Input
            id="drafterEmailId"
            value={formData.drafterEmailId}
            onChange={(event) => {
              setFormData({
                ...formData,
                drafterEmailId: event.target.value,
              });
            }}
          />
          <p className="dls-red">{drafterEmailValidator}</p>
        </div>
      </ModalBody>
      <ModalFooter>
        <ButtonPrimary className="margin-1-r" onClick={onSaveAsDraftClick}>
          Save as Draft
        </ButtonPrimary>
        <ButtonPrimary onClick={toggleDrafterEmailModal}>Cancel</ButtonPrimary>
      </ModalFooter>
    </Modal>
  );

  const requesterCommentsModal = (
    <Modal onClose={toggleModal}>
      <ModalHeader id="modal-header">
        <h2 className="fluid heading-4"></h2>
      </ModalHeader>
      <ModalBody>
        <div className="pad-3">
          <h2 className="heading-5 margin-3-b pad-3-t">
            <b>Requester Comments</b>
          </h2>
          <p>{formData.approverComment}</p>
        </div>
      </ModalBody>
      <ModalFooter>
        <ButtonPrimary onClick={toggleModal}>Continue</ButtonPrimary>
      </ModalFooter>
    </Modal>
  );

  const saveAsDraftButtonElement =
    page !== 6 ? (
      <ButtonPrimary className="dls-button" onClick={toggleDrafterEmailModal}>
        Save as Draft
      </ButtonPrimary>
    ) : (
      <div></div>
    );

  const nextButtonElement =
    page < 6 ? (
      <ButtonPrimary
        className="margin-1-r dls-button"
        type={submitOrButton(page, 5)}
        onClick={onNextClick}
      >
        {nextOrSubmit(page, 5, isEditClicked)}
      </ButtonPrimary>
    ) : (
      <div />
    );
  const backButtonElement =
    page !== 1 && page !== 6 && isEditClicked === false ? (
      <ButtonPrimary className="margin-1-r dls-button" onClick={onBackClick}>
        Back
      </ButtonPrimary>
    ) : (
      <div />
    );

  const saveAsDraftSuccessNotification =
    showDraftSuccess === true ? (
      <PageLevelMessage
        className="margin-2-r margin-2-l"
        type="success"
        dismissible={true}
        filled={true}
      >
        All Changes saved!
      </PageLevelMessage>
    ) : (
      <div></div>
    );

  const initialDisplay = () => {
    switch (page) {
      case 0:
        return <Loading />;
      case -1:
        return <ErrorBoundary />;
      case -2:
        return <InvalidSupplier />;
      default:
        return <Loading />;
    }
  };

  // TODO: move the ternary operator to a method.
  return page < 1 ? (
    initialDisplay()
  ) : (
    <div>
      <form className="vendorform" onSubmit={handleSubmit}>
        <HelpDialogue />
        <MultiStepTracker
          className="margin-15-left-right"
          currentStep={page}
          label="Application Process"
        >
          <Step label="Supplier Contact Information" />
          <Step label="Supplier Information" />
          <Step label="Supplier Banking Information" />
          <Step label="American Express Contact Information" />
          <Step label="Review & Success" />
        </MultiStepTracker>
        <div className="new-line" />
        {saveAsDraftSuccessNotification}
        {alertNotification}
        {showValidationsModal && validationsModal}
        <div className="col-xs-12 col-md-12 col-lg-5 margin-responsive pad-0">
          {showSubmissionLoadingModal && (
            <Modal>
              <ModalHeader dismissible={false} id="modal-header">
                <h2 className="fluid heading-4 height-6-vh"></h2>
              </ModalHeader>
              <ModalBody>
                <div className="pad-3">
                  <h2>Please wait, Your submission is in progress.</h2>
                  <br />
                  <Loading />
                </div>
              </ModalBody>
            </Modal>
          )}
          {PageDisplay(
            page,
            setPage,
            isNextClicked,
            setIsEditClicked,
            formData,
            setFormData,
            formErrors,
            setFormErrors,
            files,
            setFiles,
            alreadyAttachedFiles,
            setAlreadyAttachedFiles,
            attachmentPlaceholder,
            staticData,
            uploadedDocs,
            setUploadedDocs,
            deletedDocGuids,
            setDeletedDocGuids,
            isAcknowledged,
            setIsAcknowledged
          )}
        </div>
        <div className="margin-4-l button-container">
          {backButtonElement}
          {nextButtonElement}
          {saveAsDraftButtonElement}
          {showRequesterComments && requesterCommentsModal}
          {showDrafterEmailModal && drafterEmailModal}
        </div>
      </form>
      {/* TODO: Uncomment askGSM chat bot ocne stream ui is shifted in this repo */}
      {/* <div
        id="chat-btn"
        className="float-right margin-2-r position-fixed-bottom-right"
        onClick={() => initiate_chat_widget({ guid: 123, channel: 'stream' })}
      >
        <IconChat
          size="lg"
          className="margin-2-r"
          theme={{
            color: '#006fcf !important',
            fill: '#006fcf !important'
          }}
          height={50}
          width={50}
        >
        </IconChat>
        <div className="new-line" />
        <Label>Ask GSM</Label>
      </div> */}
    </div>
  );
};

export default VendorIntakeForm;
